import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabase';
import { useAuth } from './AuthContext';
import { 
  FiAlertCircle, 
  FiShield, 
  FiArrowLeft,
  FiCheck 
} from 'react-icons/fi';
import { FaGoogle } from 'react-icons/fa';
import './auth-pages.css';

function ReauthenticatePage() {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { markUserAsReauthenticated, getNextPageAfterReauth } = useAuth();

  const handleEmailReauth = async (event) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error("No active session");
      }
      
      // Reauthenticate with current credentials
      const { error } = await supabase.auth.signInWithPassword({
        email: session.user.email,
        password: password
      });

      if (error) throw error;
      
      // Mark as reauthenticated and navigate
      markUserAsReauthenticated();
      navigate(getNextPageAfterReauth() || '/dashboard/profile');
    } catch (error) {
      setError('Authentication failed: ' + (error.message || 'Please check your password'));
      setIsLoading(false);
    }
  };

  const handleGoogleReauth = async () => {
    setError('');
    setIsLoading(true);
    
    try {
      // For Google sign-in, we'll use OAuth
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/dashboard/profile'
        }
      });

      if (error) throw error;
      
      // Google OAuth will handle the redirect, so we'll mark as reauthenticated
      // in the useEffect of the component that gets loaded after redirect
      markUserAsReauthenticated();
    } catch (error) {
      setError('Authentication failed: ' + (error.message || 'Google sign-in failed'));
      setIsLoading(false);
    }
  };

  // Check if user signed in with Google
  const checkIsGoogleUser = async () => {
    const { data } = await supabase.auth.getUser();
    if (data && data.user) {
      const { identities } = data.user;
      return identities && identities.some(identity => identity.provider === 'google');
    }
    return false;
  };

  const [isGoogleUser, setIsGoogleUser] = useState(false);
  
  React.useEffect(() => {
    checkIsGoogleUser().then(isGoogle => setIsGoogleUser(isGoogle));
  }, []);

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Verify Your Identity</h1>
          <p>For security reasons, please confirm your password</p>
        </div>

        {error && (
          <div className="auth-error">
            <FiAlertCircle />
            <span>{error}</span>
          </div>
        )}

        <div className="auth-warning" style={{ marginBottom: '1.5rem' }}>
          <FiShield />
          <span>
            This action requires recent authentication. Please verify your identity to continue.
          </span>
        </div>

        {isGoogleUser ? (
          <button 
            className="auth-btn google-btn" 
            onClick={handleGoogleReauth}
            disabled={isLoading}
          >
            <FaGoogle /> {isLoading ? 'Verifying...' : 'Verify with Google'}
          </button>
        ) : (
          <form className="auth-form" onSubmit={handleEmailReauth}>
            <div className="form-group">
              <input
                type="password"
                className="form-control"
                placeholder="Your Current Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <button 
              type="submit" 
              className="auth-btn primary-btn"
              disabled={isLoading}
            >
              <FiCheck /> {isLoading ? 'Verifying...' : 'Verify Password'}
            </button>
          </form>
        )}

        <button 
          className="auth-btn secondary-btn" 
          onClick={() => navigate('/dashboard/profile')}
          disabled={isLoading}
        >
          <FiArrowLeft /> Back to Profile
        </button>
      </div>
    </div>
  );
}

export default ReauthenticatePage;